import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../main/baseQueryWithReauth';
import type { PresentationAudienceCatalogResponse } from '../../presentation/models';
import { partnerCreativesDeskApi } from '../creatives-desk/creativesDesk.api';
import type {
  BriefAudienceListRequest,
  BriefAudiencesParams,
  BriefFileResponse,
  BriefIntakeResponse,
  BriefItemResponse,
  ConfirmBriefRequest,
  DeleteBriefFileRequest,
  DeleteBriefItemRequest,
  FillBriefsRequest,
  GetBriefAdExamplesResponse,
  GetBriefsParams,
  GetBriefsResponse,
  StartBriefIntakeRequest,
  StoreBriefFileRequest,
  StoreBriefItemRequest,
  UpdateBriefAudiencesRequest,
  UpdateBriefItemRequest,
} from './models';

const partnerBriefs = `partner_briefs`;

const briefCacheId = (type: number, eventId?: number | null) => `${type}:${eventId ?? 'global'}`;

export const partnerBriefsApi = createApi({
  reducerPath: partnerBriefs,
  baseQuery: baseQueryWithReauth,
  tagTypes: [partnerBriefs],
  endpoints: (build) => ({
    getBriefs: build.query<GetBriefsResponse, GetBriefsParams>({
      query: ({ type, event_id }) => ({
        url: '/partner/briefs',
        method: 'GET',
        params: event_id ? { type, event_id } : { type },
        headers: {
          Accept: 'application/json',
        },
      }),
      providesTags: (_result, _error, { type, event_id }) => [
        { type: partnerBriefs, id: briefCacheId(type, event_id) },
      ],
    }),
    fillBriefs: build.mutation<GetBriefsResponse, FillBriefsRequest>({
      query: ({ type, event_id, answers }) => ({
        url: '/partner/briefs',
        method: 'PATCH',
        body: event_id ? { type, event_id, answers } : { type, answers },
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }),
      async onQueryStarted({ type, event_id }, { dispatch, queryFulfilled }) {
        try {
          const { data: response } = await queryFulfilled;
          dispatch(
            partnerBriefsApi.util.updateQueryData(
              'getBriefs',
              event_id ? { type, event_id } : { type },
              () => response,
            ),
          );
        } catch {
          /* mutation failed — cache stays as is */
        }
      },
    }),
    // the sponsor confirms an answer an agent filed for them — the response is
    // the same resolved brief, so it replaces the cached one
    confirmBrief: build.mutation<GetBriefsResponse, ConfirmBriefRequest>({
      query: ({ type, event_id, question }) => ({
        url: '/partner/briefs/confirm',
        method: 'PATCH',
        body: event_id ? { type, event_id, question } : { type, question },
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }),
      async onQueryStarted({ type, event_id }, { dispatch, queryFulfilled }) {
        try {
          const { data: response } = await queryFulfilled;
          dispatch(
            partnerBriefsApi.util.updateQueryData(
              'getBriefs',
              event_id ? { type, event_id } : { type },
              () => response,
            ),
          );
        } catch {
          /* mutation failed — cache stays as is */
        }
      },
    }),
    // ── the rows and files of the shaped questions ─────────────────────────
    // every write invalidates the brief it belongs to: the rows, the files and
    // the mirrored text answer all change together on the backend
    storeBriefItem: build.mutation<BriefItemResponse, StoreBriefItemRequest & { type: number }>({
      query: ({ type: _type, ...body }) => ({
        url: '/partner/briefs/items',
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      }),
      invalidatesTags: (_result, _error, { type, event_id }) => [
        { type: partnerBriefs, id: briefCacheId(type, event_id) },
      ],
    }),
    updateBriefItem: build.mutation<BriefItemResponse, UpdateBriefItemRequest>({
      query: ({ itemId, type: _type, event_id: _eventId, ...body }) => ({
        url: `/partner/briefs/items/${itemId}`,
        method: 'PATCH',
        body,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      }),
      invalidatesTags: (_result, _error, { type, event_id }) => [
        { type: partnerBriefs, id: briefCacheId(type, event_id) },
      ],
    }),
    deleteBriefItem: build.mutation<void, DeleteBriefItemRequest>({
      query: ({ itemId }) => ({
        url: `/partner/briefs/items/${itemId}`,
        method: 'DELETE',
        headers: { Accept: 'application/json' },
      }),
      invalidatesTags: (_result, _error, { type, event_id }) => [
        { type: partnerBriefs, id: briefCacheId(type, event_id) },
      ],
    }),
    storeBriefFile: build.mutation<BriefFileResponse, StoreBriefFileRequest>({
      query: ({ type: _type, event_id, question, brief_item_id, role, file }) => {
        const form = new FormData();
        form.append('file', file);
        if (event_id) form.append('event_id', String(event_id));
        if (question) form.append('question', String(question));
        if (brief_item_id) form.append('brief_item_id', String(brief_item_id));
        if (role !== undefined) form.append('role', String(role));
        return {
          url: '/partner/briefs/files',
          method: 'POST',
          body: form,
          headers: { Accept: 'application/json' },
        };
      },
      invalidatesTags: (_result, _error, { type, event_id }) => [
        { type: partnerBriefs, id: briefCacheId(type, event_id) },
      ],
    }),
    deleteBriefFile: build.mutation<void, DeleteBriefFileRequest>({
      query: ({ fileId }) => ({
        url: `/partner/briefs/files/${fileId}`,
        method: 'DELETE',
        headers: { Accept: 'application/json' },
      }),
      invalidatesTags: (_result, _error, { type, event_id }) => [
        { type: partnerBriefs, id: briefCacheId(type, event_id) },
      ],
    }),
    // "who the campaign reaches": the event's audience catalog, the sponsor's
    // own audiences beside it, and what the brief has picked
    getBriefAudiences: build.query<PresentationAudienceCatalogResponse, BriefAudiencesParams>({
      query: ({ event_id }) => ({
        url: '/partner/briefs/audiences',
        method: 'GET',
        params: { event_id },
        headers: { Accept: 'application/json' },
      }),
      providesTags: (_result, _error, { event_id }) => [
        { type: partnerBriefs, id: `audiences:${event_id}` },
      ],
    }),
    // the pick replaces the question's rows; the desk's forecast reads it, so
    // the plan refreshes too
    updateBriefAudiences: build.mutation<
      PresentationAudienceCatalogResponse,
      UpdateBriefAudiencesRequest
    >({
      query: ({ type: _type, event_id, audience_ids }) => ({
        url: '/partner/briefs/audiences',
        method: 'PUT',
        body: { event_id, audience_ids },
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      }),
      async onQueryStarted({ event_id }, { dispatch, queryFulfilled }) {
        try {
          const { data: response } = await queryFulfilled;
          dispatch(
            partnerBriefsApi.util.updateQueryData(
              'getBriefAudiences',
              { event_id },
              () => response,
            ),
          );
          dispatch(
            partnerCreativesDeskApi.util.invalidateTags([
              { type: 'partner_creatives_desk', id: 'PLAN' },
            ]),
          );
        } catch {
          /* mutation failed — cache stays as is */
        }
      },
      invalidatesTags: (_result, _error, { type, event_id }) => [
        { type: partnerBriefs, id: briefCacheId(type, event_id) },
      ],
    }),
    // the sponsor's own target accounts: an uploaded list becomes a segment
    // and an audience, as the Audiences section makes them
    storeBriefAudienceList: build.mutation<
      PresentationAudienceCatalogResponse,
      BriefAudienceListRequest
    >({
      query: (body) => ({
        url: '/partner/briefs/audiences/lists',
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      }),
      invalidatesTags: (_result, _error, { event_id }) => [
        { type: partnerBriefs, id: `audiences:${event_id}` },
      ],
    }),
    // the "see how it's used" dictionary: per kind of ad the copy and the
    // neutral stand-ins, plus the one-time hint — static, read once
    getBriefAdExamples: build.query<GetBriefAdExamplesResponse, void>({
      query: () => ({
        url: '/partner/brief/ad-examples',
        method: 'GET',
        headers: { Accept: 'application/json' },
      }),
      keepUnusedDataFor: 3600,
    }),
    // "give us what you already have": documents, a page or a paste the account
    // manager reads into the brief — the answers land later, marked for a look
    startBriefIntake: build.mutation<BriefIntakeResponse, StartBriefIntakeRequest>({
      query: ({ type, event_id, files, url, text }) => {
        const form = new FormData();
        form.append('type', String(type));
        if (event_id) form.append('event_id', String(event_id));
        (files ?? []).forEach((file) => form.append('files[]', file));
        if (url) form.append('url', url);
        if (text) form.append('text', text);
        return {
          url: '/partner/briefs/intake',
          method: 'POST',
          body: form,
          headers: { Accept: 'application/json' },
        };
      },
      invalidatesTags: (_result, _error, { type, event_id }) => [
        { type: partnerBriefs, id: briefCacheId(type, event_id) },
      ],
    }),
    // where a started intake stands: reading → filing → done (the agent reported)
    getBriefIntake: build.query<BriefIntakeResponse, string>({
      query: (intakeId) => ({
        url: `/partner/briefs/intake/${intakeId}`,
        method: 'GET',
        headers: { Accept: 'application/json' },
      }),
      keepUnusedDataFor: 0,
    }),
  }),
});

export const {
  useGetBriefsQuery,
  useFillBriefsMutation,
  useConfirmBriefMutation,
  useStoreBriefItemMutation,
  useUpdateBriefItemMutation,
  useDeleteBriefItemMutation,
  useStoreBriefFileMutation,
  useDeleteBriefFileMutation,
  useStartBriefIntakeMutation,
  useGetBriefIntakeQuery,
  useGetBriefAdExamplesQuery,
  useGetBriefAudiencesQuery,
  useUpdateBriefAudiencesMutation,
  useStoreBriefAudienceListMutation,
} = partnerBriefsApi;
